"use client";

import { useCallback } from "react";
import { useRouter } from "next/navigation";
import { ErrorBoundary } from "@/components/common/ErrorBoundary";
import HomepageActionCard from "@/components/homepage/HomepageActionCard";
import HomepageEducationSection from "@/components/homepage/HomepageEducationSection";
import HomepageFaqSection from "@/components/homepage/HomepageFaqSection";
import HomepageHero from "@/components/homepage/HomepageHero";
import HomepageTestimonialsSection from "@/components/homepage/HomepageTestimonialsSection";
import HomepageSectionSkeleton from "@/components/ui/HomepageSectionSkeleton";
import LocationBasedItems from "@/components/ui/LocationBasedItems";
import HomepageDemandStrip from "@/components/homepage/HomepageDemandStrip";
import RecentlyConfirmedShelf from "@/components/ui/RecentlyConfirmedShelf";
import RecentlySoldItems from "@/components/ui/RecentlySoldItems";
import Button from "@/components/ui/Button";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation } from "@/contexts/LocationContext";
import { useSystemSettings } from "@/contexts/SystemSettingsContext";
import { getHomepageFeedHeadline, HOMEPAGE_COPY } from "@/lib/homepage/copy";
import { resolveHomepageLayoutVariant } from "@/lib/homepage/layoutVariant";
import {
  getVisibleHomepageSections,
  type HomepageSectionId,
} from "@/lib/homepage/sectionPlan";
import { useHomepageData } from "@/lib/hooks/useHomepageData";
import type { SearchResponse } from "@/lib/services/searchService";
import type { Item, ItemRequest } from "@/lib/types";

type HomepageViewProps = {
  onOpenNearMeAlerts: (proactive: boolean) => void;
  onItemClick: (item: Item) => void;
  onRequestClick: (request: ItemRequest) => void;
};

export default function HomepageView({
  onOpenNearMeAlerts,
  onItemClick,
  onRequestClick: _onRequestClick,
}: HomepageViewProps) {
  const router = useRouter();
  const { data, error, isLoading } = useHomepageData();
  const { recentItemRequestsEnabled } = useSystemSettings();
  const { isAuthenticated, user } = useAuth();
  const { userLocation } = useLocation();

  const { approvedItems, recentlySoldItems, recentRequests } = data;
  const feedHeadline = getHomepageFeedHeadline({
    areaName: userLocation?.areaName,
  });

  const variant = resolveHomepageLayoutVariant({
    isAuthenticated,
    hasProfileLocation: Boolean(user?.location),
  });

  const visibleSections = getVisibleHomepageSections({
    variant,
    recentItemRequestsEnabled,
    recentlySoldCount: recentlySoldItems.length,
  });

  const handleSearch = useCallback(
    async (query: string): Promise<SearchResponse> => {
      if (query.trim()) {
        router.push(`/market?search=${encodeURIComponent(query)}`);
      }
      return { items: [], total: 0 };
    },
    [router],
  );

  if (
    error &&
    !approvedItems.length &&
    !recentlySoldItems.length &&
    (!recentItemRequestsEnabled || !recentRequests.length)
  ) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] px-4">
        <p className="text-red-600 mb-4">{HOMEPAGE_COPY.errors.loadFailed}</p>
        <Button onClick={() => window.location.reload()}>
          {HOMEPAGE_COPY.errors.retry}
        </Button>
      </div>
    );
  }

  const renderSection = (id: HomepageSectionId) => {
    switch (id) {
      case "hero":
        return <HomepageHero key={id} variant={variant} />;
      case "actionCard":
        return (
          <HomepageActionCard
            key={id}
            onSearch={handleSearch}
            onBrowse={() => router.push("/market")}
            onSell={() => router.push("/sell")}
            onNearMe={() => onOpenNearMeAlerts(false)}
            onRequestItem={() => {
              if (!isAuthenticated) {
                router.push(
                  "/login?redirectTo=" + encodeURIComponent("/request-item"),
                );
                return;
              }
              router.push("/request-item");
            }}
            recentItemRequestsEnabled={recentItemRequestsEnabled}
          />
        );
      case "feed":
        return (
          <ErrorBoundary key={id}>
            <section className="w-full max-w-7xl mb-12">
              {isLoading && approvedItems.length === 0 ? (
                <HomepageSectionSkeleton title={feedHeadline.title} />
              ) : (
                <LocationBasedItems
                  items={approvedItems}
                  title={feedHeadline.title}
                  subtitle={feedHeadline.subtitle}
                  viewAllLink="/market"
                  onItemClick={onItemClick}
                  maxItemsForDistance={20}
                />
              )}
            </section>
          </ErrorBoundary>
        );
      case "recentlyConfirmed":
        return (
          <ErrorBoundary key={id}>
            <div className="w-full max-w-7xl [&:has(>_*)]:mb-12">
              <RecentlyConfirmedShelf
                viewAllLink="/market"
                onItemClick={onItemClick}
              />
            </div>
          </ErrorBoundary>
        );
      case "recentRequests":
        return (
          <ErrorBoundary key={id}>
            <section className="w-full max-w-7xl mb-12">
              {isLoading && recentRequests.length === 0 ? (
                <HomepageSectionSkeleton
                  title={HOMEPAGE_COPY.shelves.recentRequests}
                />
              ) : (
                <HomepageDemandStrip
                  requests={recentRequests}
                  title={HOMEPAGE_COPY.shelves.recentRequests}
                  emptyTitle={HOMEPAGE_COPY.shelves.recentRequestsEmptyTitle}
                  emptyBody={HOMEPAGE_COPY.shelves.recentRequestsEmptyBody}
                  emptyCtaHref="/request-item"
                  emptyCtaLabel={HOMEPAGE_COPY.shelves.recentRequestsEmptyCta}
                />
              )}
            </section>
          </ErrorBoundary>
        );
      case "recentlySold":
        return (
          <ErrorBoundary key={id}>
            <section className="w-full max-w-7xl mb-12">
              <RecentlySoldItems
                items={recentlySoldItems}
                title={HOMEPAGE_COPY.shelves.recentlySold}
              />
            </section>
          </ErrorBoundary>
        );
      case "education":
        return <HomepageEducationSection key={id} />;
      case "faq":
        return <HomepageFaqSection key={id} />;
      case "testimonials":
        return <HomepageTestimonialsSection key={id} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col items-center px-4 py-8 md:px-6 md:py-12 lg:px-8">
      {visibleSections.map(renderSection)}
    </div>
  );
}
